import React from 'react';
import { Menu, MenuItem, Box, CircularProgress, Divider } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { routes } from '../../config/utility';

const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const NotificationDropdown = ({
    anchorEl,
    open,
    onClose,
    notifications,
    loading,
    onMarkAsRead,
}) => {
    const navigate = useNavigate();

    const handleItemClick = async (notification) => {
        const bookingId = notification?.data?.booking_id;
        if (!notification.read_at) {
            await onMarkAsRead(notification.id);
        }
        onClose();
        if (bookingId) {
            navigate(routes.booking_detail.replace(':id?', String(bookingId)));
        }
    };

    return (
        <Menu
            anchorEl={anchorEl}
            keepMounted
            open={open}
            onClose={onClose}
            PaperProps={{
                style: {
                    width: '320px',
                    maxHeight: '420px',
                    borderRadius: '1.5rem',
                    background: '#fff',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 8px 32px 0 #9B5DE522',
                    padding: 0,
                },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
            <div className="p-4 border-b border-gray-200 bg-white rounded-t-2xl">
                <h4 className="text-base font-bold text-primary">Notifications</h4>
            </div>

            {loading ? (
                <Box className="flex justify-center py-8">
                    <CircularProgress size={24} className="text-primary" />
                </Box>
            ) : notifications.length === 0 ? (
                <MenuItem disabled className="py-6 px-4">
                    <span className="text-sm text-gray-500 w-full text-center">No notifications yet</span>
                </MenuItem>
            ) : (
                <div className="max-h-72 overflow-y-auto">
                    {notifications.map((notification) => (
                        <MenuItem
                            key={notification.id}
                            onClick={() => handleItemClick(notification)}
                            className={`py-3 px-4 text-dark hover:bg-primary/10 transition-all duration-200 ${
                                !notification.read_at ? 'bg-primary/5' : ''
                            }`}
                        >
                            <div className="flex flex-col gap-1 w-full min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                    <span className="text-sm font-semibold text-gray-900 truncate">
                                        {notification.data?.title || 'Notification'}
                                    </span>
                                    {!notification.read_at && (
                                        <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                                    )}
                                </div>
                                <span className="text-xs text-gray-600 line-clamp-2">
                                    {notification.data?.body}
                                </span>
                                <span className="text-xs text-gray-400">
                                    {formatRelativeTime(notification.created_at)}
                                </span>
                            </div>
                        </MenuItem>
                    ))}
                </div>
            )}

            <Divider />
            <MenuItem
                component={Link}
                to={routes.organizer_notifications}
                onClick={onClose}
                className="py-3 px-4 text-center justify-center text-primary font-semibold hover:bg-primary/10 transition-all duration-200 rounded-b-2xl"
            >
                View all
            </MenuItem>
        </Menu>
    );
};

export default NotificationDropdown;
